/*
  Comportamiento compartido del tema.
  Custom elements sin dependencias: <hv-drawer>, <hv-carousel>, <hv-tabs>,
  <hv-countdown>, <hv-sticky-bar> y la utilidad HV.cart para agregar al carrito
  y abrir el <cart-drawer> con sus secciones ya renderizadas.
*/
(() => {
  if (window.HV && window.HV.ready) return;

  const HV = (window.HV = window.HV || {});
  HV.ready = true;

  HV.reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Bloqueo de scroll ---------- */
  let lockCount = 0;
  HV.lock = () => {
    lockCount += 1;
    document.documentElement.classList.add('hv-locked');
  };
  HV.unlock = () => {
    lockCount = Math.max(0, lockCount - 1);
    if (!lockCount) document.documentElement.classList.remove('hv-locked');
  };

  /* ---------- Foco ---------- */
  const FOCUSABLE =
    'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), summary';

  const focusables = (root) =>
    Array.from(root.querySelectorAll(FOCUSABLE)).filter(
      (el) => el.offsetWidth || el.offsetHeight || el.getClientRects().length
    );

  const traps = new WeakMap();

  HV.trapFocus = (container, initial) => {
    HV.releaseFocus(container);
    const onKeydown = (event) => {
      if (event.key !== 'Tab') return;
      const items = focusables(container);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    container.addEventListener('keydown', onKeydown);
    traps.set(container, onKeydown);
    const target = initial || focusables(container)[0] || container;
    if (target === container && !container.hasAttribute('tabindex')) container.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  };

  HV.releaseFocus = (container, returnTo) => {
    const handler = traps.get(container);
    if (handler) container.removeEventListener('keydown', handler);
    traps.delete(container);
    if (returnTo && typeof returnTo.focus === 'function') returnTo.focus({ preventScroll: true });
  };

  // cart-drawer.js usa trapFocus/removeTrapFocus globales.
  let globalTrap = null;
  if (typeof window.trapFocus !== 'function') {
    window.trapFocus = (container, initial) => {
      globalTrap = container;
      HV.trapFocus(container, initial);
    };
  }
  if (typeof window.removeTrapFocus !== 'function') {
    window.removeTrapFocus = (returnTo) => {
      if (globalTrap) HV.releaseFocus(globalTrap, returnTo);
      globalTrap = null;
    };
  }

  /* ---------- Dinero ---------- */
  HV.money = (cents) => {
    const value = Math.round(Number(cents) || 0) / 100;
    const hasDecimals = Math.round(value * 100) % 100 !== 0;
    return (
      '$' +
      value.toLocaleString('en-US', {
        minimumFractionDigits: hasDecimals ? 2 : 0,
        maximumFractionDigits: 2,
      })
    );
  };

  /* ---------- Drawer ---------- */
  class HvDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.hv-drawer__panel');
      this._onKeydown = (event) => {
        if (event.key === 'Escape') this.close();
      };
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-hv-close]')) {
          event.preventDefault();
          this.close();
        }
      });
      this._bindSwipe();
    }

    get isOpen() {
      return this.hasAttribute('open');
    }

    open(trigger) {
      if (this.isOpen) return;
      this.trigger = trigger || document.activeElement;
      document.querySelectorAll('hv-drawer[open]').forEach((drawer) => drawer.close({ silent: true }));
      this.setAttribute('open', '');
      this.setAttribute('aria-hidden', 'false');
      if (this.trigger && this.trigger.setAttribute) this.trigger.setAttribute('aria-expanded', 'true');
      HV.lock();
      if (this._sheet) this._sheet.opened();
      document.addEventListener('keydown', this._onKeydown);
      const initial = this.querySelector('[data-hv-autofocus]');
      setTimeout(() => HV.trapFocus(this.panel || this, initial), 60);
      this.dispatchEvent(new CustomEvent('hv:drawer:open', { bubbles: true }));
    }

    close(options = {}) {
      if (!this.isOpen) return;
      this.removeAttribute('open');
      this.setAttribute('aria-hidden', 'true');
      if (this.trigger && this.trigger.setAttribute) this.trigger.setAttribute('aria-expanded', 'false');
      HV.unlock();
      document.removeEventListener('keydown', this._onKeydown);
      HV.releaseFocus(this.panel || this, options.silent ? null : this.trigger);
      this.dispatchEvent(new CustomEvent('hv:drawer:close', { bubbles: true }));
    }

    toggle(trigger) {
      this.isOpen ? this.close() : this.open(trigger);
    }

    // En móvil la hoja se arrastra para expandir, regresar o cerrar (HV.sheet).
    _bindSwipe() {
      const handle = this.querySelector('[data-hv-swipe]');
      if (!handle || !this.panel) return;
      this._sheet = HV.sheet(this.panel, handle, {
        media: '(max-width: 989px)',
        close: () => this.close(),
      });
      this.addEventListener('hv:drawer:close', () => {
        setTimeout(() => {
          if (!this.isOpen) this.panel.classList.remove('is-expanded');
        }, 400);
      });
    }
  }

  /* ---------- Hoja inferior arrastrable (móvil) ---------- */
  // Reglas de las hojas de iOS, tomadas de Vaul (el estándar en web):
  // - Se arrastra desde cualquier punto del panel, no solo desde el asa.
  // - Hacia abajo, primero se desplaza el contenido; la hoja baja solo cuando
  //   el contenido ya está hasta arriba (y no en los 100 ms después de un scroll).
  // - Hacia arriba la hoja pasa a pantalla completa; ya completa, desplaza el contenido.
  // - Gestos horizontales (carruseles, chips) y los primeros 400 ms tras abrir
  //   no arrastran.
  // - Al soltar: se cierra si bajó más de 25 % o con velocidad > 0.4 px/ms.
  // - Un toque en el asa alterna entre alto normal y completo.
  // El alto expandido vive en .is-expanded (CSS). Devuelve { opened() } para
  // que el dueño avise cuándo se abrió.
  HV.sheet = (panel, handle, { media, close }) => {
    const mq = window.matchMedia(media);
    const EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';
    const SETTLE = `height 0.5s ${EASE}, transform 0.5s ${EASE}`;
    const VELOCITY = 0.4;
    const CLOSE_SHARE = 0.25;
    const overlay = panel.parentElement && panel.parentElement.querySelector('.hv-drawer__overlay, .hvm-cart__overlay');
    let drag = null;
    let openedAt = 0;
    let scrolledAt = 0;

    panel.addEventListener('scroll', () => (scrolledAt = performance.now()), { capture: true, passive: true });

    const heightFor = (expanded) => {
      const had = panel.classList.contains('is-expanded');
      const inline = panel.style.height;
      panel.style.height = '';
      panel.classList.toggle('is-expanded', expanded);
      const height = panel.getBoundingClientRect().height;
      panel.classList.toggle('is-expanded', had);
      panel.style.height = inline;
      return height;
    };

    const resetOverlay = () => {
      if (!overlay) return;
      overlay.style.transition = `opacity 0.5s ${EASE}`;
      overlay.style.opacity = '';
      setTimeout(() => (overlay.style.transition = ''), 520);
    };

    const settle = (expanded, closing) => {
      clearTimeout(panel._sheetTimer);
      if (closing) {
        // Cierra desde donde quedó el dedo, no desde arriba.
        panel.style.transition = '';
        if (overlay) overlay.style.opacity = '';
        close();
        requestAnimationFrame(() => {
          panel.style.transform = '';
        });
        panel._sheetTimer = setTimeout(() => {
          panel.style.height = '';
          panel.style.maxHeight = '';
          panel.classList.remove('is-expanded');
          if (overlay) overlay.style.transition = '';
        }, 520);
        return;
      }
      resetOverlay();
      panel.style.transition = SETTLE;
      panel.style.maxHeight = 'none';
      panel.style.height = `${heightFor(expanded)}px`;
      panel.style.transform = '';
      panel._sheetTimer = setTimeout(() => {
        panel.classList.toggle('is-expanded', expanded);
        panel.style.transition = 'none';
        panel.style.height = '';
        panel.style.maxHeight = '';
        requestAnimationFrame(() => {
          panel.style.transition = '';
        });
      }, 510);
    };

    // ¿El contenido bajo el dedo puede desplazarse en esa dirección?
    const scrollableAncestor = (target) => {
      for (let el = target; el && el !== panel.parentElement; el = el.parentElement) {
        if (el.scrollHeight > el.clientHeight + 1) {
          const overflow = getComputedStyle(el).overflowY;
          if (overflow === 'auto' || overflow === 'scroll') return el;
        }
      }
      return null;
    };

    const shouldDrag = (state, dx, dy) => {
      if (Math.abs(dx) > Math.abs(dy)) return false;
      if (state.target.closest('select, [data-hv-no-drag]')) return false;
      if (state.onHandle) return true;
      if (performance.now() - openedAt < 400) return false;
      const selection = window.getSelection && window.getSelection().toString();
      if (selection) return false;
      const scroller = scrollableAncestor(state.target);
      if (dy < 0) return !state.expanded && !scroller;
      if (scroller && scroller.scrollTop > 0) return false;
      if (performance.now() - scrolledAt < 100) return false;
      return true;
    };

    const begin = (x, y, time, target) => {
      if (!mq.matches) return;
      clearTimeout(panel._sheetTimer);
      drag = {
        x,
        y,
        target,
        onHandle: handle.contains(target),
        expanded: panel.classList.contains('is-expanded'),
        decided: false,
        samples: [{ y, t: time }],
      };
    };

    const start = (y) => {
      drag.y = y;
      drag.start = panel.getBoundingClientRect().height;
      drag.rest = heightFor(false);
      drag.full = heightFor(true);
      // El max-height del CSS frenaría el arrastre hacia el alto completo.
      panel.style.maxHeight = 'none';
      panel.style.transition = 'none';
      if (overlay) overlay.style.transition = 'none';
    };

    const move = (y, time) => {
      drag.samples.push({ y, t: time });
      if (drag.samples.length > 6) drag.samples.shift();
      let height = drag.start - (y - drag.y);
      let offset = 0;
      // Resistencia al pasar del alto completo.
      if (height > drag.full) height = drag.full + (height - drag.full) * 0.2;
      if (height < drag.rest) {
        offset = drag.rest - height;
        height = drag.rest;
      }
      if (Math.round(height) !== Math.round(panel.getBoundingClientRect().height)) panel.style.height = `${height}px`;
      panel.style.transform = offset ? `translate3d(0, ${offset}px, 0)` : '';
      if (overlay) overlay.style.opacity = String(Math.max(0, 1 - offset / drag.rest));
    };

    const finish = (y, time) => {
      const state = drag;
      drag = null;
      // Velocidad de los últimos ~120 ms de movimiento (no hasta que se suelta:
      // si el dedo se detuvo antes de soltar, el tirón ya no cuenta).
      const last = state.samples[state.samples.length - 1];
      const recent = state.samples.filter((s) => last.t - s.t < 120);
      const first = recent[0];
      const still = time - last.t > 120;
      const velocity = still ? 0 : (last.y - first.y) / Math.max(1, last.t - first.t); // px/ms, + hacia abajo
      const offset = Math.max(0, y - state.y - (state.start - state.rest));
      if (offset > state.rest * CLOSE_SHARE || (offset > 0 && velocity > VELOCITY)) return settle(false, true);
      let expanded;
      if (velocity < -VELOCITY) expanded = true;
      else if (velocity > VELOCITY) expanded = false;
      else expanded = panel.getBoundingClientRect().height > (state.rest + state.full) / 2;
      settle(expanded, false);
    };

    // Táctil: todo el panel. touchmove no es pasivo para poder frenar el scroll.
    panel.addEventListener(
      'touchstart',
      (event) => {
        if (event.touches.length !== 1) {
          drag = null;
          return;
        }
        const touch = event.touches[0];
        begin(touch.clientX, touch.clientY, event.timeStamp, event.target);
      },
      { passive: true }
    );

    panel.addEventListener(
      'touchmove',
      (event) => {
        if (!drag) return;
        const touch = event.touches[0];
        if (!drag.decided) {
          const dx = touch.clientX - drag.x;
          const dy = touch.clientY - drag.y;
          if (!dx && !dy) return;
          drag.decided = true;
          if (!event.cancelable || !shouldDrag(drag, dx, dy)) {
            drag = null;
            return;
          }
          start(touch.clientY);
        }
        event.preventDefault();
        move(touch.clientY, event.timeStamp);
      },
      { passive: false }
    );

    const touchEnd = (event) => {
      if (!drag) return;
      const touch = event.changedTouches[0];
      if (!drag.decided) {
        const tap = drag.onHandle && !event.target.closest('button, a, input, select');
        const expanded = drag.expanded;
        drag = null;
        if (tap && event.type === 'touchend') settle(!expanded, false);
        return;
      }
      finish(touch.clientY, event.timeStamp);
    };
    panel.addEventListener('touchend', touchEnd);
    panel.addEventListener('touchcancel', touchEnd);

    // Mouse (pruebas en escritorio angosto): solo desde el asa.
    handle.style.touchAction = 'none';
    handle.style.cursor = 'grab';
    handle.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button > 0 || event.target.closest('button, a, input, select')) return;
      begin(event.clientX, event.clientY, event.timeStamp, event.target);
      if (!drag) return;
      handle.setPointerCapture(event.pointerId);
    });
    handle.addEventListener('pointermove', (event) => {
      if (event.pointerType !== 'mouse' || !drag) return;
      if (!drag.decided) {
        if (Math.abs(event.clientY - drag.y) < 4) return;
        drag.decided = true;
        start(event.clientY);
      }
      move(event.clientY, event.timeStamp);
    });
    const mouseEnd = (event) => {
      if (event.pointerType !== 'mouse' || !drag) return;
      if (handle.hasPointerCapture(event.pointerId)) handle.releasePointerCapture(event.pointerId);
      if (!drag.decided) {
        const expanded = drag.expanded;
        drag = null;
        settle(!expanded, false);
        return;
      }
      finish(event.clientY, event.timeStamp);
    };
    handle.addEventListener('pointerup', mouseEnd);
    handle.addEventListener('pointercancel', mouseEnd);

    return {
      opened() {
        openedAt = performance.now();
      },
    };
  };
  if (!customElements.get('hv-drawer')) customElements.define('hv-drawer', HvDrawer);

  // Disparadores: <button data-hv-open="MenuDrawer">.
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-hv-open]');
    if (!trigger) return;
    const drawer = document.getElementById(trigger.dataset.hvOpen);
    if (!drawer || typeof drawer.open !== 'function') return;
    event.preventDefault();
    drawer.toggle ? drawer.toggle(trigger) : drawer.open(trigger);
  });

  // Controles que aplican al cambiar (orden de colección y búsqueda).
  document.addEventListener('change', (event) => {
    const control = event.target.closest('[data-hv-autosubmit]');
    if (!control) return;
    if (control.form) {
      control.form.requestSubmit();
      return;
    }
    const url = new URL(window.location.href);
    url.searchParams.set(control.name, control.value);
    url.searchParams.delete('page');
    window.location.href = url.toString();
  });

  /* ---------- Vistas apiladas (submenús) ---------- */
  // <hv-views> con hijos [data-hv-view]; botones [data-hv-view-target="id"].
  class HvViews extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-hv-view-target]');
        if (!trigger || !this.contains(trigger)) return;
        event.preventDefault();
        this.show(trigger.dataset.hvViewTarget, trigger);
      });
      const drawer = this.closest('hv-drawer');
      if (drawer) drawer.addEventListener('hv:drawer:close', () => setTimeout(() => this.show('main'), 350));
    }

    show(name, trigger) {
      const target = this.querySelector(`[data-hv-view="${name}"]`);
      if (!target) return;
      this.querySelectorAll('[data-hv-view]').forEach((view) => {
        const active = view === target;
        if (active) view.hidden = false;
        view.classList.toggle('is-active', active);
        view.toggleAttribute('inert', !active);
        if (!active) {
          clearTimeout(view._hideTimer);
          view._hideTimer = setTimeout(() => {
            if (!view.classList.contains('is-active') && view.dataset.hvView !== 'main') view.hidden = true;
          }, 340);
        }
      });
      if (name !== 'main') this._lastTrigger = trigger;
      requestAnimationFrame(() => {
        const focusTarget =
          name === 'main' && this._lastTrigger ? this._lastTrigger : target.querySelector('button, a[href]');
        if (focusTarget) focusTarget.focus({ preventScroll: true });
      });
    }
  }
  if (!customElements.get('hv-views')) customElements.define('hv-views', HvViews);

  /* ---------- Carrusel ---------- */
  class HvCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-hv-track]');
      if (!this.track) return;
      this.prev = this.querySelector('[data-hv-prev]');
      this.next = this.querySelector('[data-hv-next]');
      this.dots = this.querySelector('[data-hv-dots]');
      if (this.prev) this.prev.addEventListener('click', () => this.go(-1));
      if (this.next) this.next.addEventListener('click', () => this.go(1));
      this._onScroll = () => {
        cancelAnimationFrame(this._raf);
        this._raf = requestAnimationFrame(() => this.update());
        clearTimeout(this._timer);
        this._timer = setTimeout(() => this.update(), 120);
      };
      this.track.addEventListener('scroll', this._onScroll, { passive: true });
      this._ro = new ResizeObserver(() => this.update());
      this._ro.observe(this.track);
      this.buildDots();
      this.update();
      this._bindDrag();
      if (this.dots) {
        this.dots.addEventListener('click', (event) => {
          const dot = event.target.closest('[data-hv-dots] > span');
          if (dot) this.goTo(Array.from(this.dots.children).indexOf(dot));
        });
      }
      this.querySelectorAll('[data-hv-goto]').forEach((tab) => {
        tab.addEventListener('click', () => this.goTo(Number(tab.dataset.hvGoto)));
      });
      this._startAutoplay();
    }

    disconnectedCallback() {
      if (this._ro) this._ro.disconnect();
      clearInterval(this._autoplay);
    }

    _startAutoplay() {
      const delay = Number(this.dataset.autoplay);
      if (!(delay > 0) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this._autoplay = setInterval(() => {
        if (document.hidden || this.matches(':hover') || this.contains(document.activeElement)) return;
        const count = this.items().length;
        if (count > 1) this.goTo((this.index() + 1) % count);
      }, delay);
      // Si la persona desliza a mano, deja de cambiar sola.
      this.track.addEventListener('pointerdown', () => clearInterval(this._autoplay), { once: true });
    }

    // En PC el track no se desliza con el mouse: arrastre manual que al
    // soltar avanza tantas tarjetas como se arrastró (mínimo una si pasó de
    // 40 px o fue un tirón rápido). Táctil queda nativo con scroll-snap.
    _bindDrag() {
      let start = null;
      let moved = false;
      this.track.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        if (this.maxScroll() <= 2) return;
        start = { x: event.clientX, left: this.track.scrollLeft, index: this.index(), samples: [] };
        moved = false;
      });
      window.addEventListener('pointermove', (event) => {
        if (!start) return;
        const dx = event.clientX - start.x;
        if (!moved && Math.abs(dx) < 6) return;
        if (!moved) {
          moved = true;
          clearTimeout(this._snapTimer);
          this.track.style.scrollSnapType = 'none';
          this.track.style.scrollBehavior = 'auto';
          this.classList.add('is-dragging');
        }
        start.samples.push({ x: event.clientX, t: event.timeStamp });
        if (start.samples.length > 5) start.samples.shift();
        this.track.scrollLeft = start.left - dx;
      });
      window.addEventListener('pointerup', (event) => {
        if (!start) return;
        const state = start;
        start = null;
        if (!moved) return;
        const dx = event.clientX - state.x;
        this.classList.remove('is-dragging');
        this.track.style.scrollBehavior = '';
        const first = state.samples[0] || { x: state.x, t: event.timeStamp - 1000 };
        const velocity = (event.clientX - first.x) / Math.max(1, event.timeStamp - first.t); // px/ms
        const offsets = this.offsets();
        const size = offsets.length > 1 ? offsets[1] - offsets[0] : this.track.clientWidth;
        let steps = Math.round(Math.abs(dx) / size);
        if (!steps && (Math.abs(dx) > 40 || Math.abs(velocity) > 0.5)) steps = 1;
        const target = Math.max(0, Math.min(this.lastReachable(), state.index + (dx < 0 ? steps : -steps)));
        this.goTo(target);
        this._restoreSnap();
      });
      // Evita abrir un enlace al terminar de arrastrar.
      this.track.addEventListener(
        'click',
        (event) => {
          if (moved) {
            event.preventDefault();
            event.stopPropagation();
            moved = false;
          }
        },
        true
      );
      this.track.addEventListener('dragstart', (event) => event.preventDefault());
    }

    // Regresa el scroll-snap cuando termina el desplazamiento suave. Antes
    // de tiempo, el snap jala el track a otra tarjeta a medio camino.
    _restoreSnap() {
      const restore = () => {
        clearTimeout(this._snapTimer);
        this.track.removeEventListener('scrollend', restore);
        this.track.style.scrollSnapType = '';
      };
      if ('onscrollend' in window) this.track.addEventListener('scrollend', restore, { once: true });
      this._snapTimer = setTimeout(restore, 700);
    }

    items() {
      return Array.from(this.track.children).filter((el) => el.getClientRects().length);
    }

    maxScroll() {
      return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    }

    // Posición de cada elemento relativa al inicio del scroll del track,
    // topada al máximo: las tarjetas del final comparten la última posición.
    offsets() {
      const items = this.items();
      if (!items.length) return [];
      const max = this.maxScroll();
      const base = items[0].getBoundingClientRect().left + this.track.scrollLeft;
      return items.map((item) => Math.min(max, item.getBoundingClientRect().left + this.track.scrollLeft - base));
    }

    // Último elemento al que el track puede alinearse.
    lastReachable() {
      const offsets = this.offsets();
      const max = this.maxScroll();
      const i = offsets.findIndex((offset) => offset >= max - 2);
      return i === -1 ? Math.max(0, offsets.length - 1) : i;
    }

    step() {
      const offsets = this.offsets();
      if (offsets.length < 2) return this.track.clientWidth;
      const gap = offsets[1] - offsets[0] || this.track.clientWidth;
      const perView = Math.max(1, Math.floor((this.track.clientWidth + 1) / gap));
      return gap * perView;
    }

    go(direction) {
      this.track.scrollBy({ left: direction * this.step(), behavior: 'smooth' });
    }

    goTo(i) {
      const offsets = this.offsets();
      if (offsets[i] === undefined) return;
      this.track.scrollTo({ left: offsets[i], behavior: 'smooth' });
    }

    index() {
      const offsets = this.offsets();
      const left = this.track.scrollLeft;
      let best = 0;
      let bestDistance = Infinity;
      offsets.forEach((offset, i) => {
        const distance = Math.abs(offset - left);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = i;
        }
      });
      return best;
    }

    // Un punto por posición alcanzable: con dos tarjetas a la vista, el
    // último punto se enciende al llegar al final.
    buildDots() {
      if (!this.dots) return;
      const count = this.items().length ? this.lastReachable() + 1 : 0;
      if (this.dots.children.length === count) return;
      this.dots.innerHTML = '';
      for (let i = 0; i < count; i += 1) this.dots.appendChild(document.createElement('span'));
    }

    update() {
      if (!this.track) return;
      const max = this.maxScroll() - 2;
      if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
      if (this.next) this.next.disabled = this.track.scrollLeft >= max;
      this.toggleAttribute('data-static', max <= 0);
      const active = this.index();
      if (this.dots) {
        this.buildDots();
        Array.from(this.dots.children).forEach((dot, i) => dot.classList.toggle('is-active', i === active));
      }
      this.querySelectorAll('[data-hv-goto]').forEach((tab) => {
        tab.setAttribute('aria-selected', String(Number(tab.dataset.hvGoto) === active));
      });
    }
  }
  if (!customElements.get('hv-carousel')) customElements.define('hv-carousel', HvCarousel);

  /* ---------- Pestañas ---------- */
  // <hv-tabs> con [role=tab][aria-controls] y paneles [role=tabpanel].
  // Si el tablist trae [data-hv-thumb], ese elemento se desliza bajo la
  // pestaña elegida (switch tipo iOS). El panel nuevo entra con un fundido
  // y un desplazamiento corto hacia el lado de la pestaña.
  class HvTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]')).filter((tab) => tab.closest('hv-tabs') === this);
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.select(tab));
        tab.addEventListener('keydown', (event) => this._onKey(event, tab));
      });
      const list = this.tabs[0] && this.tabs[0].closest('[role="tablist"]');
      this.thumb = list && list.querySelector('[data-hv-thumb]');
      if (this.thumb) {
        const place = () => this._placeThumb(this.tabs.find((t) => t.getAttribute('aria-selected') === 'true'));
        place();
        new ResizeObserver(place).observe(list);
        requestAnimationFrame(() => this.thumb.classList.add('is-ready'));
      }
    }

    _placeThumb(tab) {
      if (!this.thumb || !tab) return;
      this.thumb.style.width = `${tab.offsetWidth}px`;
      this.thumb.style.transform = `translateX(${tab.offsetLeft}px)`;
    }

    select(tab) {
      const current = this.tabs.find((t) => t.getAttribute('aria-selected') === 'true');
      if (current === tab) return;
      const direction = this.tabs.indexOf(tab) > this.tabs.indexOf(current) ? 1 : -1;
      this.tabs.forEach((other) => {
        const selected = other === tab;
        other.setAttribute('aria-selected', String(selected));
        other.tabIndex = selected ? 0 : -1;
        const panel = document.getElementById(other.getAttribute('aria-controls'));
        if (!panel) return;
        panel.hidden = !selected;
        if (selected && current && !HV.reduceMotion()) {
          panel.animate(
            [
              { opacity: 0, transform: `translateX(${direction * 24}px)` },
              { opacity: 1, transform: 'none' },
            ],
            { duration: 360, easing: 'cubic-bezier(0.32, 0.72, 0, 1)' }
          );
        }
      });
      this._placeThumb(tab);
      this.dispatchEvent(new CustomEvent('hv:tab', { bubbles: true, detail: { tab } }));
    }

    _onKey(event, tab) {
      const i = this.tabs.indexOf(tab);
      let next = null;
      if (event.key === 'ArrowRight') next = this.tabs[(i + 1) % this.tabs.length];
      if (event.key === 'ArrowLeft') next = this.tabs[(i - 1 + this.tabs.length) % this.tabs.length];
      if (next) {
        event.preventDefault();
        next.focus();
        this.select(next);
      }
    }
  }
  if (!customElements.get('hv-tabs')) customElements.define('hv-tabs', HvTabs);

  /* ---------- Cuenta regresiva ---------- */
  // <hv-countdown data-end="2026-10-31T23:59:59-06:00"> con [data-unit=d|h|m|s].
  class HvCountdown extends HTMLElement {
    connectedCallback() {
      this.end = Date.parse(this.dataset.end);
      if (Number.isNaN(this.end)) return;
      this.units = {};
      this.querySelectorAll('[data-unit]').forEach((el) => {
        this.units[el.dataset.unit] = el;
      });
      this.tick();
      this._interval = setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      clearInterval(this._interval);
    }

    tick() {
      let diff = Math.max(0, Math.floor((this.end - Date.now()) / 1000));
      if (diff === 0) {
        clearInterval(this._interval);
        const scope = this.closest('[data-hv-countdown-scope]') || this;
        if (this.hasAttribute('data-hide-on-end')) scope.hidden = true;
      }
      const d = Math.floor(diff / 86400);
      diff -= d * 86400;
      const h = Math.floor(diff / 3600);
      diff -= h * 3600;
      const m = Math.floor(diff / 60);
      const s = diff - m * 60;
      const pad = (n) => String(n).padStart(2, '0');
      if (this.units.d) this.units.d.textContent = d;
      if (this.units.h) this.units.h.textContent = pad(h);
      if (this.units.m) this.units.m.textContent = pad(m);
      if (this.units.s) this.units.s.textContent = pad(s);
    }
  }
  if (!customElements.get('hv-countdown')) customElements.define('hv-countdown', HvCountdown);

  /* ---------- Barra fija al bajar ---------- */
  // <hv-sticky-bar data-watch="#ProductBuy"> aparece cuando el bloque observado sale de vista.
  class HvStickyBar extends HTMLElement {
    connectedCallback() {
      this.target = document.querySelector(this.dataset.watch);
      if (!this.target) return;
      // Por posición y no por IntersectionObserver: si la página salta (scroll
      // restaurado, ancla), el observador no avisa y la barra quedaba visible.
      this._onScroll = () => {
        cancelAnimationFrame(this._raf);
        this._raf = requestAnimationFrame(() => this.check());
      };
      window.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onScroll, { passive: true });
      this.check();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onScroll);
    }

    check() {
      const past = this.target.getBoundingClientRect().bottom < 0;
      if (past === this.classList.contains('is-visible')) return;
      this.classList.toggle('is-visible', past);
      this.setAttribute('aria-hidden', String(!past));
      this.toggleAttribute('inert', !past);
    }
  }
  if (!customElements.get('hv-sticky-bar')) customElements.define('hv-sticky-bar', HvStickyBar);

  /* ---------- Barra de anuncios (rotación en móvil) ---------- */
  class HvAnnouncement extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.children);
      if (this.items.length < 2) return;
      this.index = 0;
      this.mq = window.matchMedia('(min-width: 990px)');
      this.start();
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start());
    }

    disconnectedCallback() {
      this.stop();
    }

    start() {
      this.stop();
      this.timer = setInterval(() => {
        if (this.mq.matches || document.hidden) return;
        this.items[this.index].classList.remove('is-active');
        this.index = (this.index + 1) % this.items.length;
        this.items[this.index].classList.add('is-active');
      }, Number(this.dataset.interval) || 4000);
    }

    stop() {
      clearInterval(this.timer);
    }
  }
  if (!customElements.get('hv-announcement')) customElements.define('hv-announcement', HvAnnouncement);

  /* ---------- Tarjeta de video ---------- */
  // El <video> vive en un <template> y solo se crea al tocar la tarjeta.
  class HvVideoCard extends HTMLElement {
    connectedCallback() {
      const button = this.querySelector('.hv-vcard__play');
      if (button) button.addEventListener('click', () => this.play());
    }

    play() {
      let video = this.querySelector('video');
      if (!video) {
        const template = this.querySelector('template');
        if (!template) return;
        this.appendChild(template.content.cloneNode(true));
        video = this.querySelector('video');
        video.addEventListener('ended', () => this.classList.remove('is-playing'));
      }
      document.querySelectorAll('hv-video-card.is-playing').forEach((card) => {
        if (card !== this) card.pause();
      });
      this.classList.add('is-playing');
      video.play().catch(() => {});
      video.focus({ preventScroll: true });
    }

    pause() {
      const video = this.querySelector('video');
      if (video) video.pause();
      this.classList.remove('is-playing');
    }
  }
  if (!customElements.get('hv-video-card')) customElements.define('hv-video-card', HvVideoCard);

  /* ---------- Búsqueda predictiva ---------- */
  class HvPredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[name="q"]');
      this.results = this.querySelector('#HvSearchResults');
      this.defaults = this.querySelector('[data-hv-search-default]');
      this.cache = new Map();
      if (!this.input || !this.results) return;
      this.input.addEventListener('input', () => {
        clearTimeout(this._timer);
        this._timer = setTimeout(() => this.search(this.input.value.trim()), 220);
      });
    }

    async search(term) {
      if (this._controller) this._controller.abort();
      if (term.length < 2) {
        this.results.hidden = true;
        this.results.innerHTML = '';
        if (this.defaults) this.defaults.hidden = false;
        return;
      }
      const key = term.toLowerCase();
      if (this.cache.has(key)) return this.render(this.cache.get(key));
      this._controller = new AbortController();
      const base = (window.routes && window.routes.predictive_search_url) || '/search/suggest';
      const params = new URLSearchParams({
        q: term,
        'resources[type]': 'product,query',
        'resources[limit]': '8',
        'resources[options][unavailable_products]': 'last',
        section_id: this.dataset.section || 'predictive-search',
      });
      try {
        const response = await fetch(`${base}?${params}`, { signal: this._controller.signal });
        if (!response.ok) throw new Error(response.status);
        const text = await response.text();
        const html = new DOMParser().parseFromString(text, 'text/html').querySelector('[data-hv-results]');
        const markup = html ? html.outerHTML : '';
        this.cache.set(key, markup);
        this.render(markup);
      } catch (error) {
        if (error.name !== 'AbortError') this.render('');
      }
    }

    render(markup) {
      this.results.innerHTML = markup;
      this.results.hidden = !markup;
      if (this.defaults) this.defaults.hidden = Boolean(markup);
    }
  }
  if (!customElements.get('hv-predictive-search')) customElements.define('hv-predictive-search', HvPredictiveSearch);

  /* ---------- Carrito ---------- */
  HV.cart = {
    drawer() {
      return document.querySelector('cart-drawer');
    },

    /**
     * Agrega líneas y abre el drawer con el HTML fresco de sus secciones.
     * @param {Array<{id:number, quantity:number, selling_plan?:number, properties?:object}>} items
     * @param {HTMLElement} [trigger] - Elemento que recupera el foco al cerrar.
     * @param {{open?: boolean}} [options] - open:false agrega sin abrir el drawer (luego HV.cart.open).
     */
    async add(items, trigger, options = {}) {
      const drawer = this.drawer();
      const sections =
        drawer && typeof drawer.getSectionsToRender === 'function'
          ? drawer.getSectionsToRender().map((section) => section.id)
          : [];
      const response = await fetch(`${(window.routes && window.routes.cart_add_url) || '/cart/add'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections, sections_url: window.location.pathname }),
      });
      const data = await response.json();
      if (!response.ok || data.status) {
        const error = new Error(data.description || data.message || 'No pudimos agregar el producto');
        error.data = data;
        throw error;
      }
      if (window.publish && window.PUB_SUB_EVENTS) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', cartData: data });
      }
      document.dispatchEvent(new CustomEvent('hv:cart:added', { detail: { items, data } }));
      if (options.open !== false) this.open(data, trigger);
      return data;
    },

    /** Abre el drawer con la respuesta de /cart/add.js (incluye sus secciones). */
    open(data, trigger) {
      const drawer = this.drawer();
      if (drawer && typeof drawer.renderContents === 'function') {
        if (trigger && typeof drawer.setActiveElement === 'function') drawer.setActiveElement(trigger);
        drawer.renderContents(data);
      } else {
        window.location.href = (window.routes && window.routes.cart_url) || '/cart';
      }
    },
  };

  // Formularios simples <form data-hv-add> (tarjetas de producto, upsells).
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[data-hv-add]');
    if (!form) return;
    event.preventDefault();
    const button = form.querySelector('[type="submit"]');
    const data = new FormData(form);
    const item = { id: Number(data.get('id')), quantity: Number(data.get('quantity') || 1) };
    if (data.get('selling_plan')) item.selling_plan = Number(data.get('selling_plan'));
    if (button) button.classList.add('is-loading');
    try {
      await HV.cart.add([item], button);
    } catch (error) {
      if (button) {
        button.classList.remove('is-loading');
        button.setAttribute('title', error.message);
      }
      return;
    }
    if (button) button.classList.remove('is-loading');
  });

  /* ---------- Video de reseña de Loox ---------- */
  // El feed de Loox trae solo la portada del video. Su vista rápida trae el
  // reproductor: se abre con el script de Loox (app embed) o en otra pestaña.
  HV.openLooxReview = (id) => {
    if (!id) return;
    if (window.LOOX && typeof window.LOOX.openQV === 'function') {
      window.LOOX.openQV(id);
      return;
    }
    window.open(`https://loox.io/post/quickview/${encodeURIComponent(id)}?p=`, '_blank', 'noopener');
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-loox-review]');
    if (!trigger) return;
    event.preventDefault();
    HV.openLooxReview(trigger.dataset.looxReview);
  });

  /* ---------- Acordeón ---------- */
  // <details> nativo con altura animada al abrir y cerrar. Los del mismo
  // data-hv-group se cierran entre sí, también animados. Mientras se cierra,
  // el details sigue [open] con .is-closing (los iconos usan :not(.is-closing)).
  const ACCORDION = 'details.hv-accordion, details.hv-facets-group';
  const ACCORDION_EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';

  const closedHeight = (details) => {
    const style = getComputedStyle(details);
    const summary = details.querySelector('summary');
    return (
      summary.getBoundingClientRect().height +
      parseFloat(style.paddingTop) +
      parseFloat(style.paddingBottom) +
      parseFloat(style.borderTopWidth) +
      parseFloat(style.borderBottomWidth)
    );
  };

  HV.toggleDetails = (details, open) => {
    const from = details.getBoundingClientRect().height;
    if (details._hvAnim) details._hvAnim.cancel();
    details.classList.toggle('is-closing', !open);
    if (HV.reduceMotion()) {
      details.open = open;
      details.classList.remove('is-closing');
      return;
    }
    if (open) details.open = true;
    const to = open ? details.getBoundingClientRect().height : closedHeight(details);
    details.style.overflow = 'hidden';
    const anim = details.animate(
      { height: [`${from}px`, `${to}px`] },
      { duration: open ? 360 : 280, easing: ACCORDION_EASE }
    );
    if (open) {
      Array.from(details.children)
        .filter((child) => child.tagName !== 'SUMMARY')
        .forEach((child) =>
          child.animate(
            [
              { opacity: 0, transform: 'translateY(-4px)' },
              { opacity: 1, transform: 'none' },
            ],
            { duration: 300, delay: 60, easing: 'ease-out', fill: 'backwards' }
          )
        );
    }
    details._hvAnim = anim;
    anim.onfinish = () => {
      details._hvAnim = null;
      details.style.overflow = '';
      if (!open) {
        details.open = false;
        details.classList.remove('is-closing');
      }
    };
  };

  document.addEventListener('click', (event) => {
    const summary = event.target.closest('summary');
    const details = summary && summary.parentElement;
    if (!details || !details.matches(ACCORDION) || event.target.closest('a, input, select')) return;
    event.preventDefault();
    const opening = !details.open || details.classList.contains('is-closing');
    if (opening && details.dataset.hvGroup) {
      document.querySelectorAll(`details[data-hv-group="${details.dataset.hvGroup}"][open]`).forEach((other) => {
        if (other !== details && !other.classList.contains('is-closing')) HV.toggleDetails(other, false);
      });
    }
    HV.toggleDetails(details, opening);
  });

  /* ---------- Asa del carrito en móvil ---------- */
  const bindCartSheet = () => {
    const drawer = document.querySelector('cart-drawer');
    const panel = drawer && drawer.querySelector('.hvm-cart__panel');
    const grabber = panel && panel.querySelector('.hvm-cart__grabber');
    if (!grabber || grabber.dataset.bound) return;
    grabber.dataset.bound = 'true';
    const sheet = HV.sheet(panel, grabber, {
      media: '(max-width: 599px)',
      close: () => drawer.close(),
    });
    // Al cerrar por la X o el fondo, vuelve a abrir con su alto normal.
    new MutationObserver(() => {
      if (drawer.classList.contains('active')) {
        sheet.opened();
        return;
      }
      setTimeout(() => {
        if (!drawer.classList.contains('active')) panel.classList.remove('is-expanded');
      }, 400);
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bindCartSheet);
  else bindCartSheet();

  /* ---------- Medición de ventanas ----------
     HV.track(evento, propiedades) manda un evento a PostHog si está cargado.
     Cualquier <dialog data-track="nombre"> se mide solo:
       dialog_shown  { dialog, ...props }
       dialog_closed { dialog, action, seconds_open, ...props }
     props = los demás atributos data-track-* del diálogo (data-track-offer="3" → offer: "3").
     action = data-track-action del botón que lo cerró; si no hay botón:
       "outside" (toque fuera), "dismiss" (Esc o gesto de atrás) o "auto". */
  HV.track = (event, properties = {}) => {
    try {
      if (window.posthog && typeof window.posthog.capture === 'function') window.posthog.capture(event, properties);
    } catch (error) {
      /* La medición nunca debe romper la tienda. */
    }
  };

  const trackProps = (dialog) => {
    const props = { dialog: dialog.dataset.track };
    Object.keys(dialog.dataset).forEach((key) => {
      if (!key.startsWith('track') || key === 'track') return;
      const name = key.slice(5).replace(/^./, (c) => c.toLowerCase()).replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);
      props[name] = dialog.dataset[key];
    });
    return props;
  };

  const tracked = new WeakMap();
  const onDialogToggle = (dialog) => {
    const state = tracked.get(dialog);
    if (dialog.open && !state) {
      tracked.set(dialog, { openedAt: Date.now(), action: null });
      HV.track('dialog_shown', trackProps(dialog));
    } else if (!dialog.open && state) {
      tracked.delete(dialog);
      HV.track('dialog_closed', {
        ...trackProps(dialog),
        action: state.action || 'auto',
        seconds_open: Math.round((Date.now() - state.openedAt) / 100) / 10,
      });
    }
  };

  const setAction = (dialog, action) => {
    const state = tracked.get(dialog);
    if (state && !state.action) state.action = action;
  };

  new MutationObserver((mutations) => {
    mutations.forEach(({ target }) => {
      if (target.tagName === 'DIALOG' && target.dataset.track) onDialogToggle(target);
    });
  }).observe(document.documentElement, { subtree: true, attributes: true, attributeFilter: ['open'] });

  // Fase de captura: registra la acción antes de que el botón cierre el diálogo.
  document.addEventListener(
    'click',
    (event) => {
      const dialog = event.target.closest('dialog[data-track]');
      if (!dialog) return;
      if (event.target === dialog) return setAction(dialog, 'outside');
      const control = event.target.closest('[data-track-action]');
      if (control) setAction(dialog, control.dataset.trackAction);
    },
    true
  );
  document.addEventListener(
    'cancel',
    (event) => {
      if (event.target.matches && event.target.matches('dialog[data-track]')) setAction(event.target, 'dismiss');
    },
    true
  );
})();
